import React, { useContext, useEffect, useState } from "react"
import { Route, Switch, useRouteMatch, useHistory, useLocation } from "react-router-dom"
import { HomePage } from "./pages/HomePage"
import { AccountDetailsPage } from "./pages/AccountDetailsPage"
import { AccountPaymentPlanPage } from "./pages/AccountPaymentPlanPage"
import { AccountInvoicesPage } from "./pages/AccountInvoicesPage"
import { AccountTransactionsPage } from "./pages/AccountTransactionsPage"
import { AppDataStateProvider } from "./state/AppDataStateProvider"
import { BackButton, BackButtonProps } from "./components/BackButton"
import { Sb1Header } from "@sb1finans/components/dist/Sb1Header"
import { MessagePage } from "./messages/MessagePage"
import useApiContext from "@sb1finans/components/dist/ApiProvider"
import { AxiosError } from "axios"
import { useCurrentAgreementId } from "./utils/route-hooks"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import { CustomThemeContext } from "@sb1finans/components/dist/themes/CustomThemeContext"
import TermDatePage from "./pages/TermDatePage"
import ExtraInvoicePaymentPage from "./pages/ExtraInvoicePaymentPage"
import AccountTerminationPage from "./pages/AccountTerminationPage"

const PageWithBackButton: React.FC<BackButtonProps> = ({onBack, backButtonLabel, children}) => {
    return (
        <>
            <BackButton onBack={onBack} backButtonLabel={backButtonLabel} />
            {children}
        </>
    )
}

interface AgreementRouterProps {
    loginUrl: string,
    apiBaseUrl: string
}

export const AgreementRouter: React.FC<AgreementRouterProps> = ({loginUrl, apiBaseUrl}) => {
    console.info("Starting agreement router")
    const {path} = useRouteMatch()
    const history = useHistory()
    //const {error} = useApiContext()
    const { pathname : currentPath } = useLocation()
    const agreementId = useCurrentAgreementId()
    const {currentTheme, setCurrentTheme} = useContext(CustomThemeContext)


    const changeFavicon = (url) => {
        // Find the existing favicon element by its rel attribute
        let link = document.querySelector("link[rel*='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = url;
        // Replace or add the new favicon to the document head
        document.getElementsByTagName('head')[0].appendChild(link);
    }

    const getCurrentTheme = async () => {
        let merchantData: any = undefined
        try {
            const response = await fetch(`${apiBaseUrl}/merchant?agreementId=${agreementId}`)
            merchantData = response.ok ? await response.json() : undefined
            agreementId === "8356308c-818e-d5e6-b082-f25ebd1eac91" ? changeFavicon("https://www.sbm.no/assets/favicon/favicon.ico?v=algA80Q8mR") : changeFavicon("/favicon.ico")
        } catch (error) {
            console.error("Error fetching merchant data", error)
        }

        console.log("Merchant data:", merchantData)
        setCurrentTheme({
            ...currentTheme,
            palette: {
              ...currentTheme.palette,
              primary: {
                ...currentTheme.palette.primary,
                main: merchantData?.theme?.primaryColor ?? currentTheme.palette.primary.main
              },
              secondary: {
                  ...currentTheme.palette.secondary,
                  main: merchantData?.theme?.secondaryColor ?? currentTheme.palette.secondary.main
              }
            },
            logo: merchantData?.theme?.logoUrl ?? "https://fakturasalg.sb1finans.no/wp-content/uploads/rgb_SB1_Finans_SMN_verti_pos.png"
          })
    }

    useEffect(() => {
        getCurrentTheme()
    }, [agreementId])


    const onBack = () => {
        history.goBack()
    }

    const getLogoutUrlTail = () => {
        switch (agreementId) {
            case "8356308c-818e-d5e6-b082-f25ebd1eac91":
                return "acr_values=urn:sb1finans:oidc:method:sbm"
            case "86A44569-2776-15D2-4832-C14F7AEBB0B7":
                return "acr_values=urn:sb1finans:oidc:method:bnbank"
            default:
                return `acr_values=urn:sb1finans:oidc:method:bankid`
        }
    }
    
    const onLogout = () => {
        const logoutUrlTail = getLogoutUrlTail()
        const logoutUrl = `${loginUrl}/login?redirectUri=${window.location.origin}/${agreementId}&${logoutUrlTail}`
        window.location.href = logoutUrl
    }

    console.log("PATH: ", currentPath)

    return (
        <AppDataStateProvider>
            <Sb1Header 
                onLogout={onLogout}
                menuItems={[
                    {name: "Oversikt", path: `/${agreementId}`, isActive: !currentPath.startsWith(`/${agreementId}/meldinger`)},
                    {name: "Meldinger", path: `/${agreementId}/meldinger`, isActive: currentPath.startsWith(`/${agreementId}/meldinger`)},
                ]} 
            />
            { /*error && <Alert severity="error">{"Vi har problemer med å hente dine kontoer."}</Alert> */}
            <Box sx={{marginBottom: 2}} />
            <Switch>
                <Route path={`${path}`} exact>
                    <HomePage />
                </Route>
                <Route path={`${path}/meldinger`} exact>
                    <MessagePage />
                </Route>
                <Route path={`${path}/kontoer/:accountId`} exact>
                    <PageWithBackButton onBack={onBack} backButtonLabel="Til oversikt">
                        <AccountDetailsPage />
                    </PageWithBackButton>
                </Route>
                <Route path={`${path}/kontoer/:accountId/transaksjoner`} exact>
                    <PageWithBackButton onBack={onBack} backButtonLabel="Til detaljer">
                        <AccountTransactionsPage />
                    </PageWithBackButton>
                </Route>
                <Route path={`${path}/kontoer/:accountId/betalingsplan`} exact>
                    <PageWithBackButton onBack={onBack} backButtonLabel="Til detaljer">
                        <AccountPaymentPlanPage />
                    </PageWithBackButton>
                </Route>
                <Route path={`${path}/kontoer/:accountId/fakturaer`} exact>
                    <PageWithBackButton onBack={onBack} backButtonLabel="Til detaljer">
                        <AccountInvoicesPage />
                    </PageWithBackButton>
                </Route>
                <Route path={`${path}/kontoer/:accountId/forfallsdato`} exact>
                    <PageWithBackButton onBack={onBack} backButtonLabel="Til detaljer">
                        <TermDatePage />
                    </PageWithBackButton>
                </Route>
                <Route path={`${path}/kontoer/:accountId/innbetaling`} exact>
                    <PageWithBackButton onBack={onBack} backButtonLabel="Til detaljer">
                        <ExtraInvoicePaymentPage />
                    </PageWithBackButton>
                </Route>
                <Route path={`${path}/kontoer/:accountId/innfri`} exact>
                    <PageWithBackButton onBack={onBack} backButtonLabel="Til detaljer">
                        <AccountTerminationPage />
                    </PageWithBackButton>
                </Route>
            </Switch>
        </AppDataStateProvider>
    )
}
